"use client";

import React, { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type TimelineStep = {
  number: string;
  title: string;
  description: string;
  image: string;
};
const steps: TimelineStep[] = [{
  number: "01",
  title: "Consultation & Vision",
  description: "We begin with a free consultation to understand your property's potential and your personal aesthetic, laying the groundwork for a bespoke landscape design.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1668432/img/campos_consultation_phase.png"
}, {
  number: "02",
  title: "Design & Material Selection",
  description: "Our experts craft a detailed plan, selecting premium sod, custom patio stones, and seasonal flora that harmonize with your home's architecture.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1668432/img/campos_design_planning.png"
}, {
  number: "03",
  title: "Execution & Installation",
  description: "From precision grading to irrigation system setup, our team transforms your outdoor space with meticulous attention to every blade of grass and stone.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1668432/img/campos_execution_landscape.png"
}];
export default function FromPrepToPublish() {
  const sectionRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollableDistance = rect.height;
      const scrolledDistance = windowHeight / 2 - rect.top;
      let currentProgress = scrolledDistance / totalScrollableDistance * 100;
      currentProgress = Math.max(0, Math.min(100, currentProgress));
      setProgress(currentProgress);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="224600" id="from-vision-to-verdant" ref={sectionRef} className="bg-background py-24 md:py-32 overflow-hidden relative text-foreground">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/5 rounded-lg blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 md:mb-32 transition-all duration-1000 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <h2 className="text-4xl md:text-6xl font-default font-bold text-primary mb-6 tracking-tight">
            From Vision to Verdant
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            Experience the seamless transition from a blank canvas to a thriving outdoor sanctuary. Campos V Landscaping brings organic precision to every project.
          </p>
        </div>

        {}
        <div ref={containerRef} className="relative">
          
          {}
          <div className="absolute left-[2.25rem] top-0 h-full w-1 md:left-0 md:top-[3.5rem] md:h-1 md:w-full bg-muted rounded-md z-0">
            {}
            <div className="absolute left-0 top-0 bg-secondary shadow-lg transition-all duration-150 ease-out rounded-md md:h-full md:w-[var(--progress)] h-[var(--progress)] w-full" style={{
            "--progress": `${progress}%`
          } as React.CSSProperties} />
          </div>

          {}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-8 relative z-10">
            {steps.map((step, i) => <TimelineNode key={i} step={step} index={i} progress={progress} />)}
          </div>

        </div>
      </div>
    </section>;
}
function TimelineNode({
  step,
  index,
  progress
}: {
  step: TimelineStep;
  index: number;
  progress: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const nodeThreshold = index * 33;
  const isActive = progress > nodeThreshold;
  return <div ref={ref} data-index={index} className={`flex flex-col items-start md:items-center relative transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      {}
      <div className={`size-20 rounded-lg bg-background border-4 flex items-center justify-center text-2xl font-bold font-default mb-8 transition-all duration-500 z-10
          ${isActive ? 'border-secondary text-secondary shadow-xl scale-110' : 'border-muted text-muted-foreground scale-100'}
        `}>
        {step.number}
      </div>

      {}
      <div className="flex flex-col gap-6 w-full pl-24 md:pl-0 text-left md:text-center">
        
        {}
        <div className={`rounded-2xl overflow-hidden border bg-card/40 backdrop-blur-md p-2 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 scale-100 shadow-2xl border-border/50' : 'opacity-0 scale-95 border-transparent'}`}>
          <div className="relative rounded-lg overflow-hidden bg-muted aspect-[4/3]">
            <img src={step.image} alt={step.title} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
            {}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent pointer-events-none" />
          </div>
        </div>

        {}
        <div className="space-y-3">
          <h3 className={`text-2xl font-default font-bold transition-colors duration-300 ${isActive ? 'text-primary' : 'text-primary/70'}`}>
            {step.title}
          </h3>
          <p className="text-muted-foreground font-default leading-relaxed">
            {step.description}
          </p>
        </div>

      </div>
    </div>;
}